import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import flow from 'lodash/flow';
import find from 'lodash/find';
import classNames from 'clsx';
import withScrollPosition from '../../decorators/withScrollPosition';
import connectWithExperiments from '../../decorators/connectWithExperiments';
import SearchIcon from '../shared/svgIcons/SearchIcon';
import { selectIsMobile, selectTopBannerHeight } from '../../selectors/app';
import { selectShouldShowDesktopUpdateBanner } from '../../selectors/desktop';
import { selectValidatedHeroCarouselItems } from '../../selectors/heroCarousel';
import { NEW_LANDING_UPSELL_ENABLED } from '../../constants/experiments/upsell';
import cssVariables from '../../styles/variables';
import icons from '../../styles/icons.module.scss';
import TuneInLogo from './TuneInLogo';
import css from './header.module.scss';

export const Header = ({
  showSidebarToggle,
  onSidebarToggle,
  isMinimalHeader,
  unlinkedLogo,
  isLanding,
  positionThree,
  positionOne,
  positionTwo,
  offset, // scrollOffset
  toggleScrollListener,
  isLeftSideOpen,
  shouldShowDesktopUpdateBanner,
  topBannerHeight,
  carouselElements,
  isDesktop,
  isMobile,
  [NEW_LANDING_UPSELL_ENABLED]: newLandingUpsellEnabled,
}) => {
  useEffect(() => {
    toggleScrollListener(!isLeftSideOpen);
  }, [toggleScrollListener, isLeftSideOpen]);

  const customHomeHeader = isLanding && !positionTwo;
  const areColorsInverted =
    !!newLandingUpsellEnabled ||
    !!find(carouselElements, (o) => o.bgColor === cssVariables['--black']);

  let logoColor = areColorsInverted
    ? 'white'
    : cssVariables['--secondary-color-5'];
  logoColor = customHomeHeader ? logoColor : cssVariables['--t-sharp'];

  if (isMinimalHeader) {
    return (
      <div
        className={classNames(css.header, css.minimalHeader)}
        data-testid={positionThree ? 'scrolledHeader' : 'header'}
      >
        <div className={css.tuneInLogoContainer} data-testid="minimal-header">
          <TuneInLogo
            unlinkedLogo={unlinkedLogo}
            logoColor={logoColor}
            isDesktop={isDesktop}
          />
        </div>
      </div>
    );
  }

  const headerIconColor =
    customHomeHeader && !areColorsInverted
      ? cssVariables['--secondary-color-5']
      : 'white';
  const topBannerOffset = shouldShowDesktopUpdateBanner
    ? { top: topBannerHeight }
    : {};

  const scrollTopOffset = {
    offset: isLanding ? 0 : offset * -1,
  };

  const headerStyling = {
    ...scrollTopOffset,
    ...topBannerOffset,
  };

  return (
    <div
      className={classNames(css.header, {
        [css.isLanding]: isLanding,
        [css.positionOne]: isLanding && positionOne,
        [css.positionTwo]: isLanding && positionTwo,
        [css.positionThree]: isLanding && positionThree,
        [css.colorsInverted]: areColorsInverted,
        [css.mobile]: isMobile,
      })}
      data-testid={positionThree ? 'scrolledHeader' : 'header'}
      style={headerStyling}
    >
      <div className={css.headerWrapper}>
        {showSidebarToggle && (
          <div
            data-testid="sidebarToggleWrapper"
            className={css.sidebarToggleIconContainer}
            onClick={onSidebarToggle}
          >
            <i id="sidebarToggle" className={icons['icon-menu']} />
          </div>
        )}
        <div className={css.tuneInLogoContainer}>
          <TuneInLogo iconProps={{ fill: logoColor }} />
        </div>
        <Link
          to="/search/"
          data-testid="searchOpen"
          className={css.searchToggleContainer}
        >
          <SearchIcon fill={headerIconColor} width="26px" height="26px" />
        </Link>
      </div>
    </div>
  );
};

Header.propTypes = {
  positionThree: PropTypes.bool,
  positionOne: PropTypes.bool,
  positionTwo: PropTypes.bool,
  offset: PropTypes.number,
  toggleScrollListener: PropTypes.func,
  onSidebarToggle: PropTypes.func.isRequired,
  showSidebarToggle: PropTypes.bool,
  isMinimalHeader: PropTypes.bool,
  unlinkedLogo: PropTypes.bool,
  isLanding: PropTypes.bool,
  sourceGuideId: PropTypes.string,
  shouldShowDesktopUpdateBanner: PropTypes.bool,
  topBannerHeight: PropTypes.number,
  carouselElements: PropTypes.array,
  isDesktop: PropTypes.bool,
  isMobile: PropTypes.bool.isRequired,
  isLeftSideOpen: PropTypes.bool,
  [NEW_LANDING_UPSELL_ENABLED]: PropTypes.bool.isRequired,
};

function mapStateToProps(state) {
  return {
    shouldShowDesktopUpdateBanner: selectShouldShowDesktopUpdateBanner(state),
    topBannerHeight: selectTopBannerHeight(state),
    carouselElements: selectValidatedHeroCarouselItems(state),
    isMobile: selectIsMobile(state),
  };
}

export default flow(
  connectWithExperiments([NEW_LANDING_UPSELL_ENABLED]),
  withScrollPosition({ verticalBreakpoint1: 100, verticalBreakpoint3: 460 }),
  connect(mapStateToProps),
)(Header);
